<script lang="ts">
  import { faArrowUpRightFromSquare, faClock, faLocationDot, faRoute } from '@fortawesome/free-solid-svg-icons'
  import { onMount } from 'svelte'
  import { getI18n } from '../../i18n'
  import { BUTTON_LINK_VARIANTS, OUTLINE_BUTTON_LINK_VARIANTS } from '../../categories'
  import { getPageCategory } from '../../pageCategory'
  import type { SerializedMapListBlock } from '../../types'
  import { parseGpx, type Coordinate } from '../../utils/gpx'
  import { formatDistance, formatMinutes, formatNumber, googleMapsDirectionsUrl, measureRoute, walkingMinutes } from '../../utils/route'
  import ButtonLink from '../ButtonLink.svelte'
  import CopyAddressButton from '../CopyAddressButton.svelte'
  import Icon from '../Icon.svelte'
  import MapMarker from '../MapMarker.svelte'
  import MapView from '../MapView.svelte'
  import MirroredIcon from '../MirroredIcon.svelte'
  import PlaceAddress from '../PlaceAddress.svelte'

  let { block }: { block: SerializedMapListBlock } = $props()

  const i18n = getI18n()
  const pageCategory = getPageCategory()

  let mapView: ReturnType<typeof MapView>
  let selectedIndex = $state<number | null>(null)
  let route = $state<Coordinate[] | null>(null)
  const measurement = $derived(route && route.length >= 2 ? measureRoute(route, block.places) : null)

  // The GPX file is fetched in the browser only; without it the block still shows its places.
  onMount(() => {
    if (!block.trackUrl) return

    const controller = new AbortController()
    fetch(block.trackUrl, { signal: controller.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((text) => (route = parseGpx(text)))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) console.error(error)
      })

    return () => controller.abort()
  })

  function duration(meters: number): string {
    return formatMinutes(walkingMinutes(meters), i18n.locale)
  }
</script>

<div class="map-list-block">
  <div class="map-list-block-map">
    <MapView places={block.places} variant={pageCategory.variant} numbered {route} bind:selectedIndex bind:this={mapView} />
  </div>

  {#if measurement}
    <div class="map-list-block-route">
      <ul class="map-list-block-facts">
        <li class="map-list-block-fact"><Icon icon={faRoute} />{formatDistance(measurement.length, i18n.locale)}</li>
        <li class="map-list-block-fact"><Icon icon={faClock} />{i18n.t('map.route.walkingTime', { duration: duration(measurement.length) })}</li>
        <li class="map-list-block-fact">
          <Icon icon={faLocationDot} />{i18n.t('map.route.stops', { count: formatNumber(block.places.length, i18n.locale) })}
        </li>
      </ul>
      <ButtonLink href={googleMapsDirectionsUrl(block.places)} target="_blank" rel="noopener noreferrer" variant={BUTTON_LINK_VARIANTS[pageCategory.variant]}>
        {i18n.t('map.routeInGoogleMaps')}
        {#snippet end()}<MirroredIcon icon={faArrowUpRightFromSquare} />{/snippet}
      </ButtonLink>
    </div>
  {/if}

  <ol class="map-list-block-places">
    {#each block.places as place, index (index)}
      <li
        class={['map-list-block-place', {
          '-selected': selectedIndex === index,
          '-variant-brand': pageCategory.variant === 'brand',
          '-variant-warning': pageCategory.variant === 'warning',
          '-variant-success': pageCategory.variant === 'success',
        }]}
      >
        {#if measurement && index > 0}
          <p class="map-list-block-leg">
            <span class="map-list-block-leg-line"></span>
            {i18n.t('map.route.leg', { distance: formatDistance(measurement.legs[index - 1], i18n.locale), duration: duration(measurement.legs[index - 1]) })}
          </p>
        {/if}
        <div class="map-list-block-row">
          <button
            type="button"
            class="map-list-block-select"
            aria-label={i18n.t('map.showPlace', { name: place.name })}
            aria-pressed={selectedIndex === index}
            onclick={() => mapView.showPlace(index)}
          >
            <span class="map-list-block-marker">
              <MapMarker icon={place.icon} variant={pageCategory.variant} number={formatNumber(index + 1, i18n.locale)} active={selectedIndex === index} />
            </span>
            <PlaceAddress {place} />
          </button>
          <div class="map-list-block-actions">
            <CopyAddressButton {place} variant={pageCategory.variant} iconOnly />
            <ButtonLink href={place.googleMapsUrl} target="_blank" rel="noopener noreferrer" variant={OUTLINE_BUTTON_LINK_VARIANTS[pageCategory.variant]}>
              <MirroredIcon icon={faArrowUpRightFromSquare} />
              <span class="map-list-block-hidden-label">{i18n.t('page.openInGoogleMaps')}</span>
            </ButtonLink>
          </div>
        </div>
      </li>
    {/each}
  </ol>
</div>

<style>
  .map-list-block {
    overflow: hidden;
    margin-bottom: var(--space-9);
    border: var(--border-style-2);
    border-radius: var(--radius-3);
  }

  .map-list-block-map {
    height: 380px;
  }

  .map-list-block-route {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-5);
    padding: var(--space-6) var(--space-7);
    border-block-start: var(--border-style-2);
    background-color: var(--color-bg-muted);
  }

  .map-list-block-facts {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3) var(--space-7);
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: var(--font-size-4);
    color: var(--color-text-muted);
  }

  .map-list-block-fact {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
  }

  .map-list-block-places {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .map-list-block-place {
    position: relative;
    border-block-start: var(--border-style-2);
  }

  .map-list-block-place.-selected.-variant-brand {
    background-color: var(--color-variant-brand-muted);
  }

  .map-list-block-place.-selected.-variant-warning {
    background-color: var(--color-variant-warning-muted);
  }

  .map-list-block-place.-selected.-variant-success {
    background-color: var(--color-variant-success-muted);
  }

  .map-list-block-leg {
    display: flex;
    align-items: center;
    gap: var(--space-5);
    margin: 0;
    padding-inline: var(--space-7);
    padding-block: var(--space-3) 0;
    font-size: var(--font-size-3);
    color: var(--color-text-muted);
  }

  /* Dotted line in the marker column, connecting this place with the previous one. */
  .map-list-block-leg-line {
    flex: none;
    width: 48px;
    height: 20px;
    background: radial-gradient(circle, var(--color-gray-70) 1.5px, transparent 2px) center / 4px 6px repeat-y;
  }

  .map-list-block-place.-variant-brand .map-list-block-leg-line {
    background-image: radial-gradient(circle, var(--color-variant-brand-line) 1.5px, transparent 2px);
  }

  .map-list-block-place.-variant-warning .map-list-block-leg-line {
    background-image: radial-gradient(circle, var(--color-variant-warning-line) 1.5px, transparent 2px);
  }

  .map-list-block-place.-variant-success .map-list-block-leg-line {
    background-image: radial-gradient(circle, var(--color-variant-success-line) 1.5px, transparent 2px);
  }

  .map-list-block-row {
    display: flex;
    align-items: center;
    gap: var(--space-5);
    padding: var(--space-6) var(--space-7);
  }

  .map-list-block-select {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: var(--space-5);
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    color: inherit;
    text-align: start;
    cursor: pointer;
  }

  /* Stretches the button over the whole list item, including the leg line and the padding around the address. */
  .map-list-block-select::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  .map-list-block-select:focus-visible {
    outline: var(--border-2) solid var(--color-variant-neutral);
    outline-offset: var(--space-2);
    border-radius: var(--radius-1);
  }

  /* Leaves room for the number tag hanging below the marker. */
  .map-list-block-marker {
    flex: none;
    display: flex;
    justify-content: center;
    width: 48px;
    padding-block-end: 22px;
  }

  /* Above the stretched select button, so copying and opening Google Maps stay separate actions. */
  .map-list-block-actions {
    position: relative;
    z-index: 1;
    flex: none;
    display: flex;
    gap: var(--space-3);
  }

  .map-list-block-hidden-label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
</style>
